import {
    Component,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { eventFindInPath } from '@fi-shared/utils';
import { ListData } from '../list-data.service';
import { SideListOptionType } from '@fi-shared/types/side-list-option-type';
import { isFunction } from '@fi-shared/utils/is-function';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiFavoritesService } from '@fi-shared/fi-shared-services/fi-favorites.service';

@Component({
    selector: 'fi-items-list-row',
    templateUrl: './list-row.component.html',
    styleUrls: ['./list-row.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-items-list-row' },
    standalone: false,
})
export class ItemsListRowComponent<T extends { IsFavorite?: boolean }> implements OnChanges, OnInit, OnDestroy {
    @Input('settings') ngModel: ListData<T>;
    @Input() data: T;
    @Input() parentData: T;
    @Input() gridView: boolean = false;

    isItemChecked: boolean;
    events$ = new Subject<{ event: string; data: any }>();

    private fiFavoritesService: FiFavoritesService;

    constructor(protected injector: Injector) {
        this.fiFavoritesService = injector.get(FiFavoritesService);
    }

    ngOnInit(): void {
        this.updateIsChecked();
        this.ngModel.selectedItems$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.updateIsChecked();
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.gridView || changes.ngModel) this.updateHeadItems();
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    private updateIsChecked() {
        this.isItemChecked = this.isChecked(this.data);
    }

    private isChecked(item: T) {
        return this.ngModel.SelectedItems.indexOf(item) !== -1;
    }

    headItems = [];

    updateHeadItems() {
        if (this.gridView) {
            this.headItems = [this.ngModel.Header[0], this.ngModel.Header[this.ngModel.NameColumnIndex]];
            if (this.ngModel.GridSecondColumnIndex)
                this.headItems.push(this.ngModel.Header[this.ngModel.GridSecondColumnIndex]);

            return;
        }
        this.headItems = this.ngModel.Header;
    }

    GetRowClass() {
        const result = [];
        result.push(this.ngModel.getRowClass(this.data));
        if (this.isItemChecked) result.push('checked BrandedColorAfter');

        if (this.ngModel.ParentProperty) {
            if (this.data[this.ngModel.ParentProperty]) {
                result.push('child-item');
            } else {
                result.push('parent-item');
            }

            // if (this.isParentCollapsed(this.ngModel.getParentItem(data))) {
            //     result.push('parent-collapsed');
            // }
        }

        if (this.isRowClickable()) result.push('Ccm-Hover-Pointer');

        if (this.gridView) {
            result.push('CcmListGridView');

            if (this.isItemChecked) {
                result.push('BrandedBorderColor');
                result.push('checked');
            }
        }
        if (!this.ngModel.BorderBottom) result.push('without-bottom-border');

        // todo: add compiled dynamically
        result.push('compiled');

        return result.join(' ').trim();
    }

    GetClass(properties) {
        let _class = '';
        if (properties.Class) _class = isFunction(properties.Class) ? properties.Class(this.data) : properties.Class;

        if (properties && properties.ExtraProperties && isFunction(properties.ExtraProperties.GetClass))
            _class += ' ' + properties.ExtraProperties.GetClass(this.data);

        return _class;
    }

    IsShowCheckbox() {
        if (isFunction(this.ngModel.ShowCheckBox)) return this.ngModel.ShowCheckBox(this.data);

        return this.ngModel.ShowCheckBox;
    }

    RowClick($event: MouseEvent) {
        // add "list-row-click-no-select" to avoid row selecting on click
        if (!this.isRowClickable() || !!eventFindInPath($event, '.list-row-click-no-select')) return;

        this.updateSelectedItemsOnClick(!this.isItemChecked);
    }

    CheckboxClick(newIsChecked: boolean) {
        this.updateSelectedItemsOnClick(newIsChecked);
    }

    private updateSelectedItemsOnClick(newIsChecked: boolean) {
        this.isItemChecked = newIsChecked;
        let shouldRaiseSelectedItemsChangedEvent = this.updateSelectedItems(this.data, newIsChecked);
        if (newIsChecked && this.ngModel.MultiSelect === false) {
            // uncheck all previously checked items
            const itemsToUnselect = this.ngModel.SelectedItems.filter((selectedItem) => selectedItem !== this.data);
            itemsToUnselect.forEach((itemToUnselect) => {
                this.ngModel.SelectedItems.splice(this.ngModel.SelectedItems.indexOf(itemToUnselect), 1);
                shouldRaiseSelectedItemsChangedEvent = true;
            });
        }

        if (shouldRaiseSelectedItemsChangedEvent) {
            if (this.ngModel.MultiSelect !== false) {
                this.ngModel
                    .getItemsWithChildrenRecursively(this.ngModel.getChildrenDataItems(this.data))
                    .forEach((childItem) => {
                        this.updateSelectedItems(childItem, newIsChecked);
                    });
            }
            if (!this.ngModel.AllowUncheckedParentWithCheckedChildren) this.updateParentItemSelected(this.data);

            this.ngModel.raiseSelectedItemsChanged();
        }
    }

    private updateSelectedItems(item: T, newIsChecked: boolean) {
        const itemIndex = this.ngModel.SelectedItems.indexOf(item);
        if (!newIsChecked && itemIndex !== -1) {
            this.ngModel.SelectedItems.splice(itemIndex, 1);
            return true;
        }
        if (newIsChecked && itemIndex === -1) {
            this.ngModel.SelectedItems.push(item);
            return true;
        }
        return false;
    }

    private updateParentItemSelected(item: T) {
        const parentItem = this.ngModel.getParentItem(item);
        if (parentItem) {
            const allParentsChildren = this.ngModel.getChildrenDataItems(parentItem);
            const allChildrenChecked = !allParentsChildren.find((childItem) => !this.isChecked(childItem));
            const allChildrenUnchecked = !allParentsChildren.find((childItem) => this.isChecked(childItem));
            if (allChildrenChecked) {
                this.updateSelectedItems(parentItem, true);
                this.updateParentItemSelected(parentItem);
            } else if (allChildrenUnchecked) {
                this.updateSelectedItems(parentItem, false);
                this.updateParentItemSelected(parentItem);
            }
        }
    }

    isMinusOnParentCheckbox() {
        if (this.ngModel.AllowUncheckedParentWithCheckedChildren) return false;

        const item = this.data;
        const childrenItems = this.ngModel.getChildrenDataItems(item);

        if (childrenItems.length === 0) return false;

        const selectedItems = childrenItems.filter((childItem) => this.isChecked(childItem));
        return (
            selectedItems.length < childrenItems.length &&
            (selectedItems.length > 0 || item[this.ngModel.TogglePropertyName] === true)
        );
    }

    isRowClickable() {
        return this.IsShowCheckbox() && this.ngModel.RowIsClickable;
    }

    isHasChildren(data) {
        return this.ngModel.getChildrenDataItems(data).length > 0;
    }

    toggleChildrenItems($event, data) {
        $event.stopPropagation();
        $event.preventDefault();

        const i = this.ngModel.collapsedParents.indexOf(data);
        if (i > -1) {
            this.ngModel.collapsedParents.splice(i, 1);
            return;
        }
        this.ngModel.collapsedParents.push(data);
    }

    isParentCollapsed(data) {
        return (
            data &&
            ((this.ngModel.collapsedParents.indexOf(data) > -1 && this.ngModel.ChildrenInitiallyCollased === false) ||
                (this.ngModel.collapsedParents.indexOf(data) === -1 && this.ngModel.ChildrenInitiallyCollased === true))
        );
    }

    isWithChildren() {
        if (isFunction(this.ngModel.isWithChildren)) return this.ngModel.isWithChildren();
        return !!this.ngModel.ChildProperty;
    }

    IsShowFavorite() {
        return this.ngModel.ShowFavoriteIcon;
    }

    isFavoriteClickInProcess = false;
    FavoriteClicked(item, $event) {
        $event.stopPropagation();

        if (!this.ngModel.ObjectType)
            throw 'Can not delete or save favorite because an "ObjectType" property is not set';

        if (this.isFavoriteClickInProcess) return;

        this.isFavoriteClickInProcess = true;
        const promise = item.IsFavorite
            ? this.fiFavoritesService.deleteFavorite(item.Id, this.ngModel.ObjectType)
            : this.fiFavoritesService.saveFavorite(item.Id, this.ngModel.ObjectType);

        return promise
            .then(() => {
                item.IsFavorite = !item.IsFavorite;

                if (!item.IsFavorite && this.ngModel.FilterType === SideListOptionType.Favorite)
                    this.ngModel.Data.splice(this.ngModel.Data.indexOf(item), 1);
            })
            .finally(() => {
                this.isFavoriteClickInProcess = false;
                this.ngModel.favoriteChanged$.next();
            });
    }
}
